import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import MainFont from 'ui/typography/mainFont';

import validator from 'utils/regex-validator';

import {
  DeleteIcon,
  DragDropContainer,
  FileContainer,
  FileDetailsContainer,
  ImportContainer,
  InputContainer,
  ItemContainer,
  MaxDimension,
  TextContainer,
  TitleContainer,
  UploadLink
} from './style';

const DragDrop = ({
  title,
  titleAlign = 'center',
  blueLink,
  handleError,
  setSelectedFile,
  selectedFile,
  margin,
  height = '109px',
  maxDimension,
  validExtensions,
  maxSize,
  titleType
}) => {
  const { t } = useTranslation();

  // eslint-disable-next-line no-unused-vars
  const [dragging, setDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  const dragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragging(true);
    }
  };

  const dragOver = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const dragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const fileSize = size => {
    if (size === 0) return '0 Bytes';
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(size) / Math.log(1024));
    return `${parseFloat(size / (1024 ** i).toFixed(2))} ${sizes[i]}`;
  };

  const handleFile = file => {
    const setError = () =>
      typeof handleError === 'function' && handleError(true);
    const removeError = () =>
      typeof handleError === 'function' && handleError(false);
    if (file) {
      if (setSelectedFile) {
        setSelectedFile(file);
      }

      if (validExtensions.indexOf(file.type) === -1) {
        setError();
        setErrorMessage(t('invalid_file'));
      } else if (file.size / maxSize > 1) {
        setError();
        setErrorMessage(t('file_too_big'));
      } else if (!validator.filename(file.name)) {
        setError();
        setErrorMessage(t('invalid_file_name'));
      } else {
        removeError();
        setErrorMessage('');
      }
    }
  };

  const fileDrop = e => {
    e.preventDefault();
    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const onRemoveFile = e => {
    e.preventDefault();
    if (setSelectedFile) {
      setSelectedFile();
    }

    if (typeof handleError === 'function') {
      handleError(true);
    }
  };

  const uploadFromLink = event => {
    handleFile(event?.target?.files?.[0]);
  };

  return (
    <ImportContainer margin={margin}>
      {title && (
        <TitleContainer titleAlign={titleAlign}>
          <MainFont type={titleType || '1'}>{title}</MainFont>
        </TitleContainer>
      )}

      <ItemContainer>
        <DragDropContainer
          dragging
          onDragEnter={dragEnter}
          onDragOver={dragOver}
          onDragLeave={dragLeave}
          onDrop={fileDrop}
          height={height}
        >
          <MainFont>{t('drag_file')}</MainFont>
          <TextContainer>
            <UploadLink blueLink={blueLink}>
              <MainFont type={blueLink && '4'}>{t('upload_link')}</MainFont>
            </UploadLink>
            <InputContainer
              type="file"
              accept={validExtensions.join(',')}
              onChange={uploadFromLink}
            />
          </TextContainer>
          {maxDimension && (
            <MaxDimension>
              <MainFont configuration={{ fontSize: 12 }}>
                {maxDimension}
              </MainFont>
            </MaxDimension>
          )}
        </DragDropContainer>
      </ItemContainer>
      {selectedFile && (
        <FileDetailsContainer>
          <FileContainer>
            <MainFont>{selectedFile.name}</MainFont>
            <MainFont>{fileSize(selectedFile.size)}</MainFont>
            <DeleteIcon className="icon-bin" onClick={e => onRemoveFile(e)} />
          </FileContainer>
          {errorMessage && (
            <MainFont type="errorLabel">{errorMessage}</MainFont>
          )}
        </FileDetailsContainer>
      )}
    </ImportContainer>
  );
};

DragDrop.propTypes = {
  title: PropTypes.string,
  titleAlign: PropTypes.string,
  margin: PropTypes.string,
  height: PropTypes.string,
  maxDimension: PropTypes.string,
  blueLink: PropTypes.bool,
  handleError: PropTypes.func,
  setSelectedFile: PropTypes.func,
  selectedFile: PropTypes.object,
  validExtensions: PropTypes.array,
  maxSize: PropTypes.number,
  titleType: PropTypes.string
};

export default connect(
  state => ({
    isUploading: state?.dragDrop?.isUploading
  }),
  () => ({})
)(DragDrop);
